import { useEffect, useState } from 'react';
import { precedentApi, type Precedent } from '../api';
import Select from '../components/common/Select';

// "Documents \u2192 Library" — the advocate's precedent collection. Phase 3
// ships keyword search; semantic search via embeddings hooks in later and
// keeps the same list / detail shape.

const CATEGORIES = [
  { value: 'pleading',            label: 'Pleading' },
  { value: 'notice_of_motion',    label: 'Notice of motion' },
  { value: 'heads_of_argument',   label: 'Heads of argument' },
  { value: 'letter_of_demand',    label: 'Letter of demand' },
  { value: 'covering_letter',     label: 'Covering letter' },
  { value: 'practice_note',       label: 'Practice note' },
  { value: 'opinion',             label: 'Opinion' },
  { value: 'judgment',            label: 'Judgment' },
  { value: 'contract',            label: 'Contract' },
  { value: 'other',               label: 'Other' },
];

export default function DocumentsLibrary() {
  const [precedents, setPrecedents] = useState<Precedent[]>([]);
  const [q, setQ] = useState('');
  const [category, setCategory] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState<Precedent | 'new' | null>(null);

  const load = async () => {
    setLoading(true); setError(null);
    try {
      const r = await precedentApi.list({ q: q || undefined, category: category || undefined });
      setPrecedents(r.precedents);
    } catch (e) { setError((e as Error).message); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [category]);

  const remove = async (p: Precedent) => {
    if (!confirm(`Delete "${p.title}"?`)) return;
    try { await precedentApi.remove(p.ROWID); load(); }
    catch (e) { alert((e as Error).message); }
  };

  return (
    <div className="stack">
      <div className="row">
        <div className="grow">
          <h1>Library</h1>
          <p className="muted">Your chambers\u2019 precedent library \u2014 reusable drafts, briefs, judgments, and letters. Searchable by title, summary, and tags.</p>
        </div>
        <button className="primary" onClick={() => setEditing('new')}>+ Precedent</button>
      </div>

      <div className="row">
        <input
          className="grow"
          placeholder="Search title, summary, tags\u2026"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && load()}
        />
        <div style={{ width: 220 }}>
          <Select
            value={category}
            onChange={setCategory}
            options={[{ value: '', label: 'All categories' }, ...CATEGORIES]}
            searchPlaceholder="Filter by category\u2026"
          />
        </div>
        <button onClick={load}>Search</button>
      </div>

      {error && <div className="card" style={{ borderColor: 'var(--color-danger)' }}>{error}</div>}

      <div className="card" style={{ padding: 0 }}>
        {loading ? (
          <div className="muted" style={{ padding: 14 }}>Loading\u2026</div>
        ) : precedents.length === 0 ? (
          <p className="muted" style={{ padding: 14 }}>No precedents yet. Save your first reusable draft to speed up future matters.</p>
        ) : (
          <table>
            <thead>
              <tr><th>Title</th><th>Category</th><th>Tags</th><th>Updated</th><th style={{ width: 160 }}></th></tr>
            </thead>
            <tbody>
              {precedents.map((p) => (
                <tr key={p.ROWID}>
                  <td style={{ fontWeight: 500 }}>
                    {p.title}
                    {p.summary && <div className="muted" style={{ fontSize: 12, marginTop: 2 }}>{p.summary}</div>}
                  </td>
                  <td><span className="pill">{CATEGORIES.find((c) => c.value === p.category)?.label || p.category}</span></td>
                  <td>{p.tags || <span className="muted">\u2014</span>}</td>
                  <td>{p.MODIFIEDTIME?.slice(0, 10) || <span className="muted">\u2014</span>}</td>
                  <td style={{ textAlign: 'right' }}>
                    <button onClick={() => setEditing(p)}>Edit</button>
                    <button onClick={() => remove(p)} style={{ marginLeft: 6 }}>Delete</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {editing && (
        <PrecedentModal
          precedent={editing === 'new' ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }}
        />
      )}
    </div>
  );
}

function PrecedentModal({
  precedent, onClose, onSaved,
}: { precedent: Precedent | null; onClose: () => void; onSaved: () => void }) {
  const [form, setForm] = useState<Partial<Precedent>>(
    precedent ? { ...precedent } : { title: '', category: 'pleading', summary: '', body: '', tags: '' }
  );
  const [full, setFull] = useState<Precedent | null>(precedent);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    // Load full body when editing — list API omits it.
    if (precedent) {
      precedentApi.get(precedent.ROWID)
        .then((r) => { setFull(r.precedent); setForm(r.precedent); })
        .catch(() => null);
    }
  }, [precedent]);

  const save = async () => {
    setSaving(true); setError(null);
    try {
      if (full) await precedentApi.update(full.ROWID, form);
      else      await precedentApi.create(form);
      onSaved();
    } catch (e) { setError((e as Error).message); }
    finally { setSaving(false); }
  };

  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)',
      display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 100,
    }} onClick={onClose}>
      <div className="card" style={{ width: 760, maxWidth: '95vw', maxHeight: '90vh', overflow: 'auto' }} onClick={(e) => e.stopPropagation()}>
        <h3>{full ? 'Edit precedent' : 'New precedent'}</h3>
        <div className="stack">
          <div className="row">
            <div className="grow">
              <label>Title</label>
              <input value={form.title || ''} onChange={(e) => setForm({ ...form, title: e.target.value })} />
            </div>
            <div style={{ width: 220 }}>
              <label>Category</label>
              <Select
                value={form.category || 'pleading'}
                onChange={(v) => setForm({ ...form, category: v })}
                options={CATEGORIES}
              />
            </div>
          </div>
          <div>
            <label>Summary (shown in list view)</label>
            <input value={form.summary || ''} onChange={(e) => setForm({ ...form, summary: e.target.value })} placeholder="Single-line description" />
          </div>
          <div>
            <label>Tags (comma-separated)</label>
            <input value={form.tags || ''} onChange={(e) => setForm({ ...form, tags: e.target.value })} placeholder="e.g. spoliation, urgent, mandament" />
          </div>
          <div>
            <label>Body</label>
            <textarea rows={16} value={form.body || ''} onChange={(e) => setForm({ ...form, body: e.target.value })} style={{ fontFamily: 'ui-monospace, monospace', fontSize: 12 }} />
          </div>
          {error && <div style={{ color: 'var(--color-danger)' }}>{error}</div>}
          <div className="row" style={{ justifyContent: 'flex-end' }}>
            <button onClick={onClose} disabled={saving}>Cancel</button>
            <button className="primary" onClick={save} disabled={saving || !form.title}>
              {saving ? 'Saving\u2026' : (full ? 'Save' : 'Create')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
